import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useProgrammes, useProducts } from "@/hooks/useCatalogue";
import PageIntro from "@/components/PageIntro";
import Reveal from "@/components/Reveal";

const statusLabel = { open: "Open for applications", waitlist: "Waitlist open", closed: "Closed", completed: "Completed" };

export default function Programmes() {
  const { data: programmes = [] } = useProgrammes();
  const { data: products = [] } = useProducts();
  const productOf = (id) => products.find((p) => p.id === id);
  return (
    <div>
      <PageIntro
        eyebrow="Experience"
        title="Experience programmes"
        description="Programmes open periodically. Each shows its associated product, eligibility, cohort dates, participation terms, and any cost or shipping terms before you apply. Membership does not guarantee a product."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="programmes-list">
        {programmes.length === 0 ? (
          <div className="border border-forest/20 p-1"><div className="border border-forest/10 px-8 py-16 text-center">
            <p className="font-serif font-light text-3xl text-forest">No programmes are open right now</p>
            <p className="mt-3 text-charcoal/65 max-w-md mx-auto font-light">Join the community and we'll invite you when a new experience programme opens.</p>
            <Link to="/join" className="mt-8 inline-flex btn btn-primary">{BRAND.joinCta}</Link>
          </div></div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {programmes.map((pr, i) => {
              const product = productOf(pr.product_id);
              return (
                <Reveal key={pr.slug} delay={i * 0.1}>
                  <Link to={`/programmes/${pr.slug}`} data-testid={`programme-card-${pr.slug}`} className="group block border border-forest/15 bg-cream hover:border-forest/50 transition-colors h-full">
                    {product?.image && (
                      <div className="aspect-[5/3] bg-linen overflow-hidden"><img src={product.image} alt="" className="w-full h-full object-contain p-6 mix-blend-multiply transition-transform duration-1000 group-hover:scale-105" /></div>
                    )}
                    <div className="p-7">
                      <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.22em]">
                        <span className={pr.status === "open" ? "text-moss" : "text-champagne"}>{statusLabel[pr.status] || pr.status}</span>
                        {product && <span className="text-charcoal/45">{product.name}</span>}
                      </div>
                      <h3 className="mt-4 font-serif font-light text-3xl text-forest leading-tight">{pr.title}</h3>
                      <p className="mt-3 text-sm text-charcoal/65 font-light">{pr.summary}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-forest link-underline">View programme <ArrowRight size={13} /></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
